import { createSlice } from "@reduxjs/toolkit";
import {
    fetchFarmhouses,
    fetchFarmhouseDetails,
    addNewFarmhouse,
    updateFarmhouseDetails,
    removeFarmhouse,
    updateFarmhouseStatus,
    updateImagePriorities,
} from "./thunk";

const initialState = {
    farmhouses: [],
    farmhouseDetails: null,
    pagination: {
        page: 1,
        limit: 10,
        totalPages: 0,
        totalFarmhouses: 0,
    },
    loading: false,
    error: "",
    success: "",
};

const FarmhouseSlice = createSlice({
    name: "Farmhouse",
    initialState,
    reducers: {
        resetFarmhouseFlag(state) {
            state.success = null;
            state.error = null;
        },
        clearFarmhouseDetails(state) {
            state.farmhouseDetails = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Farmhouses
            .addCase(fetchFarmhouses.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(fetchFarmhouses.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload && action.payload.data) {
                    if (Array.isArray(action.payload.data)) {
                        state.farmhouses = action.payload.data;
                        state.pagination = {
                            ...state.pagination,
                            totalFarmhouses: action.payload.data.length,
                            totalPages: 1
                        };
                    } else if (action.payload.data.farmhouses) {
                        state.farmhouses = action.payload.data.farmhouses;

                        const paginationData = action.payload.data.pagination || action.payload.data;

                        state.pagination = {
                            page: paginationData.page || state.pagination.page,
                            limit: paginationData.limit || state.pagination.limit,
                            totalPages: paginationData.totalPages ||
                                Math.ceil((paginationData.total || paginationData.totalFarmhouses || 0) /
                                    (paginationData.limit || state.pagination.limit)),
                            totalFarmhouses: paginationData.total || paginationData.totalFarmhouses ||
                                paginationData.totalCount || action.payload.data.farmhouses.length
                        };
                    }
                }
            })
            .addCase(fetchFarmhouses.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to fetch farmhouses";
            })

            // Fetch Farmhouse Details
            .addCase(fetchFarmhouseDetails.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(fetchFarmhouseDetails.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload && action.payload.data) {
                    state.farmhouseDetails = action.payload.data;
                }
            })
            .addCase(fetchFarmhouseDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to fetch farmhouse details";
            })

            // Add Farmhouse
            .addCase(addNewFarmhouse.pending, (state) => {
                state.loading = true;
                state.error = "";
                state.success = "";
            })
            .addCase(addNewFarmhouse.fulfilled, (state, action) => {
                state.loading = false;
                state.success = "Farmhouse created successfully";
            })
            .addCase(addNewFarmhouse.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to create farmhouse";
            })

            // Update Farmhouse
            .addCase(updateFarmhouseDetails.pending, (state) => {
                state.loading = true;
                state.error = "";
                state.success = "";
            })
            .addCase(updateFarmhouseDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.success = "Farmhouse updated successfully";
            })
            .addCase(updateFarmhouseDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to update farmhouse";
            })

            // Delete Farmhouse
            .addCase(removeFarmhouse.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(removeFarmhouse.fulfilled, (state, action) => {
                state.loading = false;
                state.farmhouses = state.farmhouses.filter(
                    (farmhouse) => farmhouse.id !== action.payload
                );
                state.success = "Farmhouse deleted successfully";
            })
            .addCase(removeFarmhouse.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to delete farmhouse";
            })

            // Update Status
            .addCase(updateFarmhouseStatus.fulfilled, (state, action) => {
                state.farmhouses = state.farmhouses.map((farmhouse) =>
                    farmhouse.id === action.payload.id
                        ? { ...farmhouse, status: action.payload.status }
                        : farmhouse
                );
            })
            // Update Image Priorities
            .addCase(updateImagePriorities.pending, (state) => {
                state.loading = true;
                state.error = "";
                state.success = "";
            })
            .addCase(updateImagePriorities.fulfilled, (state) => {
                state.loading = false;
                state.success = "Image priorities updated successfully";
            })
            .addCase(updateImagePriorities.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to update image priorities";
            });
    },
});

export const { resetFarmhouseFlag, clearFarmhouseDetails } = FarmhouseSlice.actions;
export default FarmhouseSlice.reducer;
